import { useEffect, useState } from "react";
import cookie from "js-cookie";
import { Modal } from "antd";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OnlineEnquiryAPI from "../utils/api/OnlineEnquiryAPI";
import { notify_error } from "./Notify";

const formatWhen = (when) => {
  if (!when) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(when));
  } catch {
    return String(when);
  }
};

const titleCase = (str) => {
  if (!str || typeof str !== "string") return str || "—";
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatRole = (role) => {
  if (!role) return "—";
  return role
    .split(",")
    .map((r) => titleCase(r.replace(/_/g, " ")))
    .join(", ");
};

const initialsOf = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const FieldBadge = ({ field }) => {
  const isMobile = field === "mobile";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        color: isMobile ? "#047857" : "#5B3FA1",
        background: isMobile ? "#ECFDF5" : "#F3EEFA",
        border: `1px solid ${isMobile ? "#A7F3D0" : "#DDD0F2"}`,
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: isMobile ? "#10B981" : "#7C3AED",
        }}
      />
      {(field || "—").toUpperCase()}
    </span>
  );
};

const Avatar = ({ name }) => (
  <div
    style={{
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #7C3AED 0%, #663399 100%)",
      color: "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "13px",
      fontWeight: 600,
      flexShrink: 0,
      letterSpacing: "0.5px",
    }}
  >
    {initialsOf(name)}
  </div>
);

const LeadViewLogs = ({ leadId }) => {
  const roles = (cookie.get("role") || "").split(",").map((r) => r.trim());
  const isSuperAdmin = roles.includes("super_admin");

  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = () => {
    if (!leadId) return;
    setLoading(true);
    OnlineEnquiryAPI.getLeadViewLogs(leadId)
      .then((res) => {
        if (res?.data?.status) {
          setLogs(res.data.data || []);
        } else if (res?.data?.message) {
          notify_error(res.data.message);
        }
      })
      .catch(() => notify_error("Failed to load view history"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) fetchLogs();
  }, [open, leadId]);

  if (!isSuperAdmin) return null;

  return (
    <>
      <div
        style={{
          marginBottom: "16px",
          padding: "18px 20px",
          background: "#fff",
          borderRadius: "12px",
          boxShadow:
            "0 1px 3px rgba(0, 31, 51, 0.06), 0 1px 2px rgba(0, 31, 51, 0.04)",
          border: "1px solid #EEF0F4",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          fontFamily: "Inter",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              background: "#F3EEFA",
              color: "#663399",
            }}
          >
            <HistoryOutlinedIcon style={{ fontSize: "20px" }} />
          </div>
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#111827",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              View History
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6B7280",
                marginTop: "2px",
              }}
            >
              See who accessed mobile or email for this lead
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            background: "#663399",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontFamily: "Inter",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Open Log
        </button>
      </div>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
        centered
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontFamily: "Inter",
              paddingRight: "32px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                background: "#F3EEFA",
                color: "#663399",
              }}
            >
              <HistoryOutlinedIcon style={{ fontSize: "20px" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  color: "#111827",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: 1.2,
                }}
              >
                View History
              </span>
              <span
                style={{
                  marginTop: "2px",
                  fontSize: "12px",
                  color: "#6B7280",
                  fontWeight: 400,
                }}
              >
                Audit trail of contact-field reveals for this lead
              </span>
            </div>
            <span
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 10px",
                background: "#F3F4F6",
                color: "#374151",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {logs.length} {logs.length === 1 ? "VIEW" : "VIEWS"}
            </span>
          </div>
        }
        bodyStyle={{ padding: "20px 24px 24px", fontFamily: "Inter" }}
      >
        {loading ? (
          <div
            style={{
              padding: "48px 0",
              textAlign: "center",
              color: "#6B7280",
              fontSize: "13px",
            }}
          >
            Loading…
          </div>
        ) : logs.length === 0 ? (
          <div
            style={{
              padding: "40px 24px",
              textAlign: "center",
              color: "#6B7280",
              fontSize: "13px",
              background: "#F9FAFB",
              border: "1px dashed #E5E7EB",
              borderRadius: "10px",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                margin: "0 auto 12px",
                borderRadius: "50%",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9CA3AF",
                border: "1px solid #E5E7EB",
              }}
            >
              <HistoryOutlinedIcon style={{ fontSize: "22px" }} />
            </div>
            <div style={{ color: "#374151", fontWeight: 600, marginBottom: "4px" }}>
              No views yet
            </div>
            <div>No contact-field reveals have been recorded for this lead.</div>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              border: "1px solid #EEF0F4",
              borderRadius: "10px",
              background: "#fff",
            }}
          >
            {logs.map((log, i) => {
              const displayName = titleCase(log.user_name);
              return (
                <div
                  key={`${log.user_id}-${log.viewed_at}-${i}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(180px, 2fr) minmax(120px, 1fr) minmax(110px, auto) minmax(150px, auto)",
                    alignItems: "center",
                    gap: "16px",
                    padding: "14px 16px",
                    borderBottom:
                      i === logs.length - 1 ? "none" : "1px solid #F1F2F4",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#FAFAFB")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      minWidth: 0,
                    }}
                  >
                    <Avatar name={displayName} />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#111827",
                          fontSize: "13px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {displayName}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#9CA3AF",
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                          marginTop: "2px",
                        }}
                      >
                        User
                      </div>
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#1F2937",
                        fontWeight: 500,
                      }}
                    >
                      {formatRole(log.user_role)}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        marginTop: "2px",
                      }}
                    >
                      Role
                    </div>
                  </div>

                  <div>
                    <FieldBadge field={log.field} />
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#1F2937",
                        fontWeight: 500,
                      }}
                    >
                      {formatWhen(log.viewed_at)}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        marginTop: "2px",
                      }}
                    >
                      Viewed At
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
};

export default LeadViewLogs;
